import type { QueryDocumentSnapshot, Unsubscribe } from 'firebase/firestore'
import { collection, getFirestore, onSnapshot } from 'firebase/firestore'
import { onUnmounted, ref, watch, isRef, type Ref, type ComputedRef } from 'vue'
import type { PoolTicket } from '../shared/types/pool.d.js'
import { poolTicketsCollectionPath } from '../utils/poolPaths.js'

export interface UsePoolTicketsResult {
  tickets: Ref<PoolTicket[]>
}

// Reactive loader for pool tickets under subkrewes/{id}/ticket_pools/{year}/tickets
// Accepts year as string or Ref<string>/ComputedRef<string> for reactive updates
export function usePoolTickets(
  subkreweId: string,
  year: string | Ref<string> | ComputedRef<string>,
): UsePoolTicketsResult {
  const tickets = ref<PoolTicket[]>([])
  const db = getFirestore()
  let stop: Unsubscribe | undefined

  const subscribe = (yearValue: string) => {
    // Clean up previous subscription
    if (stop) {
      stop()
      stop = undefined
    }

    if (!yearValue || !subkreweId) {
      tickets.value = []
      return
    }

    try {
      const colPath = poolTicketsCollectionPath(subkreweId, yearValue)
      const colRef = collection(db, colPath)
      stop = onSnapshot(colRef, (snap) => {
        const rows: PoolTicket[] = []
        snap.forEach((doc: QueryDocumentSnapshot) => {
          const data = doc.data() as Omit<PoolTicket, 'id'>
          rows.push({ id: doc.id, ...data })
        })
        tickets.value = rows
      })
    }
    catch (err) {
      // silent failure; UI can render empty
      console.error('usePoolTickets subscribe failed', err)
    }
  }

  // Handle reactive year
  if (isRef(year)) {
    watch(year, (newYear) => {
      subscribe(newYear)
    }, { immediate: true })
  }
  else {
    subscribe(year)
  }

  onUnmounted(() => {
    if (stop) stop()
  })

  return { tickets }
}
